/**
 * SnoozeMenu Component - PRD-3596 Alert Snooze & Dismiss
 * 
 * Dropdown menu for snoozing alerts with preset duration options.
 * - 1 hour
 * - 4 hours  
 * - 24 hours
 * - 1 week
 * 
 * Uses React Query for mutations and provides immediate UI feedback.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { alertsApi, SnoozeResponse, UndoDismissResponse, DismissResponse } from '../../lib/alertsApi';

interface SnoozeMenuProps {
  alertId: string;
  organizationId: string;
  userId: string;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  position?: { x: number; y: number };
}

interface SnoozeOption {
  value: string;
  label: string;
  hours: number;
}

const SNOOZE_OPTIONS: SnoozeOption[] = [
  { value: '1h', label: '1 hour', hours: 1 },
  { value: '4h', label: '4 hours', hours: 4 },
  { value: '24h', label: '24 hours', hours: 24 },
  { value: '1w', label: '1 week', hours: 168 },
];

export const SnoozeMenu: React.FC<SnoozeMenuProps> = ({
  alertId,
  organizationId,
  userId,
  onClose,
  onSuccess,
  position,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Snooze mutation
  const snoozeMutation = useMutation({
    mutationFn: (durationHours: number) =>
      alertsApi.snooze(alertId, organizationId, userId, durationHours),
    onSuccess: (data: SnoozeResponse) => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      const snoozeUntil = data.snooze_until 
        ? new Date(data.snooze_until).toLocaleString()
        : 'later';
      onSuccess?.(`Alert snoozed until ${snoozeUntil}`);
      onClose();
    },
    onError: (error: Error) => {
      console.error('Snooze failed:', error);
    },
  });

  const handleSnooze = (option: SnoozeOption) => {
    snoozeMutation.mutate(option.hours);
  };

  // Calculate menu position (viewport-aware)
  const menuStyle: React.CSSProperties = position
    ? {
        position: 'fixed',
        left: Math.min(position.x, window.innerWidth - 200),
        top: Math.min(position.y, window.innerHeight - 220),
        zIndex: 1000,
      }
    : {
        position: 'absolute',
        right: 0,
        top: '100%',
        marginTop: '0.5rem',
        zIndex: 50,
      };

  return (
    <div
      ref={menuRef}
      className="bg-white dark:bg-boxdark rounded-lg shadow-lg border border-stroke dark:border-strokedark w-48"
      style={menuStyle}
    >
      <div className="p-2">
        <div className="text-xs text-bodydark2 px-3 py-2 border-b border-stroke dark:border-strokedark">
          Snooze for...
        </div>
        
        {SNOOZE_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => handleSnooze(option)}
            disabled={snoozeMutation.isPending}
            className="w-full text-left px-3 py-2.5 hover:bg-gray-2 dark:hover:bg-meta-4 
                       rounded-sm text-sm text-black dark:text-white
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors flex items-center gap-2"
          >
            <ClockIcon className="w-4 h-4 text-bodydark2" />
            <span>{option.label}</span>
          </button>
        ))}

        {snoozeMutation.isPending && (
          <div className="px-3 py-2 text-center text-xs text-bodydark2">
            Snoozing...
          </div>
        )}
      </div>
    </div>
  );
};

// Clock Icon
const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

/**
 * DismissButton with Undo Toast - PRD-3596
 * 
 * Dismisses an alert and shows a toast with undo option.
 * Undo is available for 10 seconds (UI) / 24 hours (backend).
 */
interface DismissButtonProps {
  alertId: string;
  organizationId: string;
  userId: string;
  reason?: string;
  onDismissed?: () => void;
  onUndoAvailable?: (undoFn: () => void) => void;
  variant?: 'button' | 'icon';
  className?: string;
}

export const DismissButton: React.FC<DismissButtonProps> = ({
  alertId,
  organizationId,
  userId,
  reason = 'User dismissed',
  onDismissed,
  onUndoAvailable,
  variant = 'button',
  className = '',
}) => {
  const queryClient = useQueryClient();

  // Undo mutation
  const undoMutation = useMutation({
    mutationFn: () => alertsApi.undoDismiss(alertId, organizationId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: (error: Error) => {
      console.error('Undo failed:', error);
    },
  });

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: () => alertsApi.dismiss(alertId, organizationId, userId, reason),
    onSuccess: (data: DismissResponse) => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      onDismissed?.();

      // Provide undo function to parent
      if (onUndoAvailable && data.undo_available) {
        onUndoAvailable(() => undoMutation.mutate());
      }
    },
    onError: (error: Error) => {
      console.error('Dismiss failed:', error);
    },
  });

  const handleDismiss = () => {
    dismissMutation.mutate();
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={handleDismiss}
        disabled={dismissMutation.isPending}
        className={`p-1.5 hover:bg-gray-2 dark:hover:bg-meta-4 rounded 
                   disabled:opacity-50 disabled:cursor-not-allowed
                   transition-colors ${className}`}
        title="Dismiss alert"
      >
        <XIcon className="w-4 h-4 text-bodydark2 hover:text-danger" />
      </button>
    );
  }

  return (
    <button
      onClick={handleDismiss}
      disabled={dismissMutation.isPending}
      className={`px-3 py-1.5 text-sm border border-stroke dark:border-strokedark
                 rounded hover:bg-gray-2 dark:hover:bg-meta-4
                 disabled:opacity-50 disabled:cursor-not-allowed
                 transition-colors ${className}`}
    >
      {dismissMutation.isPending ? 'Dismissing...' : 'Dismiss'}
    </button>
  );
};

// X Icon
const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

/**
 * SnoozeDropdown - Compact snooze button with dropdown
 */
interface SnoozeDropdownProps {
  alertId: string;
  organizationId: string;
  userId: string;
  onSuccess?: (message: string) => void;
  className?: string;
}

export const SnoozeDropdown: React.FC<SnoozeDropdownProps> = ({
  alertId,
  organizationId,
  userId,
  onSuccess,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-1.5 text-sm border border-stroke dark:border-strokedark
                   rounded hover:bg-gray-2 dark:hover:bg-meta-4
                   transition-colors flex items-center gap-1.5"
      >
        <ClockIcon className="w-4 h-4" />
        <span>Snooze</span>
        <ChevronDownIcon className="w-3 h-3" />
      </button>

      {isOpen && (
        <SnoozeMenu
          alertId={alertId}
          organizationId={organizationId}
          userId={userId}
          onClose={() => setIsOpen(false)}
          onSuccess={onSuccess}
        />
      )}
    </div>
  );
};

// Chevron Down Icon
const ChevronDownIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

/**
 * UndoToast - Simple undo toast component
 */
interface UndoToastProps {
  message: string;
  onUndo: () => void;
  onClose: () => void;
  duration?: number;
}

export const UndoToast: React.FC<UndoToastProps> = ({
  message,
  onUndo,
  onClose,
  duration = 10000,
}) => {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-black text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-4">
      <span className="text-sm">{message}</span>
      <button
        onClick={() => {
          onUndo();
          onClose();
        }}
        className="text-primary hover:underline font-medium text-sm"
      >
        Undo
      </button>
      <button
        onClick={onClose}
        className="text-gray-400 hover:text-white ml-2"
      >
        <XIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

export default SnoozeMenu;
